<sbnb-confetti #confetti></sbnb-confetti>
<ng-container *ngIf="(success$ | async) === true; else showWizard">
  <div class="flex flex-col items-center justify-center gap-9">
    <h2>You are subscribed!</h2>
    <sbnb-confirmation-summary></sbnb-confirmation-summary>
    <sbnb-button type="primary" (click)="leaveWizard()">Return to Settings</sbnb-button>
  </div>
</ng-container>
<ng-template #showWizard>
  <ng-container *ngIf="pendingSubscriptionChange$ | async as pending">
    <mat-stepper #stepper [linear]="true" [disableRipple]="true">
      <!-- Initial summary screen - this always shows as we will always have a step to confirm billing details before the final Review & confirm step -->
      <mat-step class="step" [completed]="true">
        <ng-template matStepLabel>Plan Summary</ng-template>
        <ng-template matStepContent>
          <div class="step">
            <div class="flex flex-col gap-9">
              <div class="text__large">This will be your new plan:</div>
              <sbnb-plan-summary></sbnb-plan-summary>
              <sbnb-manage-subscription-wizard-footer (cancel)="leaveWizard()" [isFirstStep]="true">
                <button mat-flat-button class="button__primary w-full sm:max-w-fit" matStepperNext>Continue</button>
              </sbnb-manage-subscription-wizard-footer>
            </div>
          </div>
        </ng-template>
      </mat-step>

      <!-- CONDITIONAL If user needs to mute properties -->
      <mat-step class="step" *ngIf="initialPropertiesEntitlement$ | async" [completed]="mutingComplete">
        <ng-template matStepLabel>Mute properties</ng-template>
        <ng-template matStepContent>
          <div class="step flex flex-col gap-6">
            <sbnb-downgrade-mute-properties
              [propertiesEntitlement]="initialPropertiesEntitlement$ | async"
              (cancel)="leaveWizard()"
              (previous)="stepper.previous()"
              (submissionSuccess)="onMutePropertiesSuccess(stepper)"></sbnb-downgrade-mute-properties>

            <sbnb-manage-subscription-wizard-footer *ngIf="mutingComplete" (cancel)="leaveWizard()">
              <button mat-flat-button class="button__primary w-full sm:max-w-fit" matStepperNext>Continue</button>
            </sbnb-manage-subscription-wizard-footer>
          </div>
        </ng-template>
      </mat-step>

      <!-- CONDITIONAL If user needs to delete secondary users -->
      <mat-step class="step" *ngIf="initialMaxSecondaryUsersEntitlement$ | async" [completed]="deletingUsersComplete">
        <ng-template matStepLabel>Delete users</ng-template>
        <ng-template matStepContent>
          <div class="step flex flex-col gap-6">
            <sbnb-downgrade-delete-users
              [maxSecondaryUsersEntitlement]="initialMaxSecondaryUsersEntitlement$ | async"
              (cancel)="leaveWizard()"
              (previous)="stepper.previous()"
              (submissionSuccess)="onDeleteUsersSuccess(stepper)"></sbnb-downgrade-delete-users>

            <sbnb-manage-subscription-wizard-footer *ngIf="deletingUsersComplete" (cancel)="leaveWizard()">
              <button mat-flat-button class="button__primary w-full sm:max-w-fit" matStepperNext>Continue</button>
            </sbnb-manage-subscription-wizard-footer>
          </div>
        </ng-template>
      </mat-step>

      <!-- CONDITIONAL If user has issues that will be resolved automatically, like parent-child relationships -->
      <ng-container *ngIf="automaticallyResolvedSteps$ | async as entitlements">
        <mat-step class="step" *ngIf="entitlements?.length" [completed]="confirmAutomaticResolutions.valid">
          <ng-template matStepLabel>Account changes</ng-template>
          <ng-template matStepContent>
            <div class="flex flex-col gap-6">
              <h3>Changes that will be made</h3>
              <div class="downgrade-card">
                <sbnb-downgrade-warning
                  *ngFor="let entitlement of entitlements"
                  [entitlement]="entitlement"></sbnb-downgrade-warning>
              </div>
              <div class="flex flex-col gap-3">
                <strong>What does this mean:</strong>
                <ul class="list">
                  <sbnb-downgrade-explanation
                    *ngFor="let entitlement of entitlements"
                    [entitlement]="entitlement"></sbnb-downgrade-explanation>
                </ul>
              </div>
              <mat-divider></mat-divider>
              <div>
                <mat-checkbox
                  style="padding: 0; align-items: start"
                  [formControl]="confirmAutomaticResolutions"
                  class="flex items-start">
                  <strong>I confirm I understand the consequences and wish to downgrade</strong>
                </mat-checkbox>
              </div>
              <sbnb-manage-subscription-wizard-footer (cancel)="leaveWizard()">
                <button
                  mat-flat-button
                  class="button__primary w-full sm:max-w-fit"
                  matStepperNext
                  [disabled]="confirmAutomaticResolutions.invalid">
                  Continue
                </button>
              </sbnb-manage-subscription-wizard-footer>
            </div>
          </ng-template>
        </mat-step>
      </ng-container>

      <!-- Billing/Payment (Final) step - we must always show billing and payment, even if they already have a method saved -->
      <mat-step [completed]="billingDetailsSaved && paymentMethodsSaved" class="step">
        <ng-template matStepLabel>Billing &amp; Payment</ng-template>
        <ng-template matStepContent>
          <div class="step">
            <ng-container>
              <div *ngIf="pending?.submitting !== 'Pending'; else submitting" class="flex flex-col gap-6">
                <div class="flex justify-between">
                  <h4>Billing Details</h4>
                  <sbnb-icon
                    icon="check"
                    class="grass-darker"
                    *ngIf="billingDetailsSaved"
                    matTooltip="Billing details saved!"></sbnb-icon>
                </div>

                <sbnb-billing-form
                  [hidden]="!showBillingDetails"
                  [embedded]="true"
                  (saved)="onBillingDetailsSaved($event)"></sbnb-billing-form>
                <mat-divider></mat-divider>
                <div class="flex justify-between">
                  <h4>Payment Methods</h4>
                  <sbnb-icon icon="check" class="grass-darker" *ngIf="paymentMethodsSaved"></sbnb-icon>
                </div>

                <ng-container *ngIf="newPlan$ | async as newPlan">
                  <sbnb-payment-cards
                    [hidden]="!showPaymentMethods"
                    [embedded]="true"
                    (saved)="onPaymentSaved($event)"
                    [planId]="newPlan.plan_id"></sbnb-payment-cards>
                </ng-container>
                <mat-divider></mat-divider>
                <sbnb-manage-subscription-wizard-footer
                  (back)="resetBillingStep()"
                  (cancel)="leaveWizard()"
                  [disabled]="submitting$ | async">
                  <button
                    mat-flat-button
                    class="button__primary w-full sm:max-w-fit"
                    (click)="changeSubscription()"
                    [disabled]="!billingDetailsSaved || !paymentMethodsSaved || (submitting$ | async)">
                    Confirm Subscription
                  </button>
                </sbnb-manage-subscription-wizard-footer>
              </div>
              <ng-template #submitting>
                <sbnb-loading-spinner minHeight="200px"></sbnb-loading-spinner>
              </ng-template>
            </ng-container>
          </div>
        </ng-template>
      </mat-step>
    </mat-stepper>
  </ng-container>
</ng-template>
